/**
 * Page PUBLIQUE d'une SKILLCARD — cible du QR code imprimé au verso
 * (https://app.jeraisonne.fr/card/{id}, spec § 9-10).
 *
 * Route enfant DIRECTE de `rootRoute` (jamais `appRoute`, qui redirige vers
 * /login) : la carte se découvre sans compte. Parcours : verso en 3D →
 * retournement → recto → progression personnelle (XP, maîtrise) → règle,
 * cours, exercices. Sans session : « Connecte-toi pour maîtriser la carte ! »
 * en haut de page, avec connexion SUR PLACE (OTP) ; pour OAuth ou un nouveau compte (détour par
 * l'onboarding), `setReturnTo` ramène ici automatiquement. Le QR ne porte que
 * l'id de la carte — jamais de donnée élève.
 */

import { ChallengeMyChildButton } from '@/features/private-live/child-challenge';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowUpLeft,
  BookOpen,
  ChevronDown,
  Dumbbell,
  GitBranch,
  ScrollText,
  Shapes,
  Star,
  Wrench,
  Pencil,
} from 'lucide-react';
import {
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  CARD_RATING_MAX,
  type CardColorTheme,
  type CardRarity,
  cardAuraScore,
  cardPalette,
  horaStatus,
  formatCardRating,
  cardUsefulnessLabel,
  formatCardRatingCount,
  manipulableCardVisuals,
  stripMath,
} from '@jeraisonne/db/cards';
import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import {
  Button,
  CompassLogo,
  cn,
  MathText,
  LessonMarkdown,
  VisualBlockRenderer,
} from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { isActiveTab } from '@/lib/active-tab';
import { useMyExerciseAttempts } from '@/lib/cards/exercise-attempts';
import { TrainStudentsPanel } from '@/features/skillcards/send-card-to-student';
import { consumeReturnTo, setReturnTo } from '@/lib/return-to';
import {
  isCardEvolved,
  useMyCardProgress,
  usePublicCard,
  useRateCard,
  type PublicCard,
  type PublicCardRating,
  type PublicCardExercise,
  type PublicCardRelative,
} from '@/lib/cards/api';
import { CardExerciseGenerator, CardExerciseList } from '@/features/cards/card-exercises';
import {
  ToolList,
  ToolboxSheet,
  contextForCards,
  linkedTools,
  toolLevelForCard,
  type ToolId,
} from '@/features/toolbox';
import { useStudentProfile } from '@/hooks/use-student-profile';
import { MasteryHero } from '@/features/skillcards/mastery-hero';
import { SkillcardUnlockPanel } from '@/features/skillcards/skillcard-unlock-panel';
import { CardAnimationBlock } from '@/features/animations/card-animation-block';
import { useSkillcardAccess } from '@/lib/skillcards/purchase';
import { CardRuleDetails, cardRuleParts } from '@/features/skillcards/card-rule-details';
import { CardFlip } from '@/features/cards/card-flip';
import { CardRecto, CardVerso } from '@/features/cards/card-faces';
import {
  CardChallengeBanner,
  CardChallengeShareButton,
  CardCircleBoard,
  CardShareButton,
} from '@/features/skillcards/card-challenge';
import {
  isChallengeToken,
  recallChallengeToken,
  rememberChallengeToken,
} from '@/lib/cards/challenge';

export function PublicCardPage() {
  const { cardId } = useParams({ strict: false }) as { cardId: string };
  // Passer d'une carte de la filiation à une autre : page neuve (retournement rejoué, haut de page).
  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [cardId]);
  return <PublicCardView key={cardId} cardId={cardId} />;
}

function PublicCardView({ cardId }: { cardId: string }) {
  const card = usePublicCard(cardId);
  // Forme ÉVOLUÉE de la créature quand l'élève connecté a atteint l'XP max
  // (même requête que le bloc « Ma maîtrise » : un seul appel).
  const { session, profile } = useAuth();
  const progress = useMyCardProgress(cardId, !!session && !!profile);
  const evolved = isCardEvolved(progress.data);
  const deckPath =
    profile?.role === 'student'
      ? ('/student/skillcards' as const)
      : profile?.role === 'player'
        ? ('/player/skillcards' as const)
        : null;
  // Admin : aperçu local des paliers d'aura (rien n'est enregistré).
  const [auraPreview, setAuraPreview] = useState<number | null>(null);
  // Défi reçu (`?defi=<jeton>`, lien partagé) : mémorisé pour survivre à la connexion.
  const [challengeToken] = useState<string | null>(() => {
    const fromUrl =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('defi')
        : null;
    if (isChallengeToken(fromUrl)) {
      rememberChallengeToken(cardId, fromUrl);
      return fromUrl;
    }
    return recallChallengeToken(cardId);
  });

  useEffect(() => {
    if (card.data) document.title = `${stripMath(card.data.card.title)} · JeRaisonne`;
  }, [card.data]);

  return (
    <div className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] min-h-dvh px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 sm:py-6">
      <div className="mx-auto max-w-xl space-y-3 sm:space-y-4">
        {/* Élève / Curieux connecté : retour direct à sa collection (le logo mène à l'accueil). */}
        {deckPath && (
          <Link
            to={deckPath}
            className="text-ink-500 hover:text-ink-800 -mb-2 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> Mes SKILLCARDS
          </Link>
        )}
        <div className="flex items-center justify-between gap-3">
          <Link
            to="/"
            aria-label="SKILLCARD by JeRaisonne — accueil"
            className="inline-flex min-h-[2.75rem] items-center gap-2"
          >
            <CompassLogo size="sm" animated={false} showWordmark={false} />
            <span className="font-display text-ink-900 text-base font-extrabold leading-none">
              SKILLCARD <span className="text-ink-500 text-sm font-semibold">by JeRaisonne</span>
            </span>
          </Link>
          {card.data && (
            <div className="flex items-center gap-2">
              <CardChallengeShareButton
                cardId={card.data.id}
                title={stripMath(card.data.card.title)}
              />
              <CardShareButton
                cardId={card.data.id}
                shortCode={card.data.short_code}
                title={stripMath(card.data.card.title)}
              />
            </div>
          )}
        </div>

        {challengeToken && card.data && (
          <CardChallengeBanner token={challengeToken} cardId={card.data.id} />
        )}

        {/* parent : défier son enfant sur cette carte (défi live privé, l'enfant est prévenu) */}
        {card.data && profile?.role === 'parent' && (
          <ChallengeMyChildButton card={{ id: card.data.id, title: card.data.card.title }} />
        )}

        {card.isLoading && (
          <div className="bg-ink-100 mx-auto aspect-[63/88] w-full max-w-[22rem] animate-pulse rounded-2xl" />
        )}

        {card.error && (
          <div role="alert" className="flex flex-col items-center gap-3 text-center">
            <p className="text-ink-600 text-sm">
              Impossible de charger la carte. Vérifie ta connexion et réessaie.
            </p>
            <button
              type="button"
              onClick={() => void card.refetch()}
              disabled={card.isFetching}
              className="bg-primary-600 min-h-[2.75rem] rounded-full px-6 text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-60"
            >
              {card.isFetching ? 'Chargement…' : 'Réessayer'}
            </button>
          </div>
        )}

        {card.data === null && (
          <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
            <p className="text-ink-900 font-semibold">Cette carte n’est pas encore disponible.</p>
            <p className="text-ink-500 mt-1 text-sm">
              Son auteur ne l’a pas encore publiée. Réessaie un peu plus tard.
            </p>
          </div>
        )}

        {card.data && (
          <>
            <h1 className="sr-only">{stripMath(card.data.card.title)}</h1>
            {!session && <CardLoginPrompt cardId={card.data.id} />}
            {session && profile && progress.isLoading && (
              <div className="bg-ink-100 h-28 animate-pulse rounded-2xl" />
            )}
            {session && profile && progress.error && (
              <p className="text-ink-500 text-center text-sm">
                Progression indisponible pour le moment.
              </p>
            )}
            {session && profile && progress.data !== undefined && !progress.error && (
              <MasteryHero
                xp={progress.data?.xp ?? 0}
                xpMax={progress.data?.xp_max ?? card.data.card.xp_max}
                mastery={progress.data?.mastery ?? 0}
                used={(progress.data?.usage_count ?? 0) > 0}
                name={profile.display_name ?? ''}
                avatarUrl={profile.avatar_url}
                accent={cardPalette(card.data.card.levels, card.data.card.color_theme).accent}
                hora={
                  progress.data
                    ? horaStatus(
                        progress.data.xp_stored ?? progress.data.xp,
                        progress.data.mastery,
                        progress.data.xp_updated_at,
                      )
                    : null
                }
                auraPower={
                  auraPreview ??
                  cardAuraScore(
                    progress.data?.mastery ?? 0,
                    progress.data?.xp ?? 0,
                    progress.data?.xp_max ?? card.data.card.xp_max,
                  )
                }
              />
            )}
            <CardFlip
              verso={
                <CardVerso
                  cardId={card.data.id}
                  shortCode={card.data.short_code}
                  title={card.data.card.title}
                  cardType={card.data.card.card_type}
                  levels={card.data.card.levels}
                  xpMax={card.data.card.xp_max}
                  rarity={card.data.card.rarity}
                  colorTheme={card.data.card.color_theme}
                  creature={card.data.card.creature}
                  evolved={evolved}
                  domainLabel={card.data.card.domain_label}
                  content={card.data.card.structured_content}
                  summaryFallback={card.data.card.rule || card.data.card.method}
                />
              }
              recto={
                <CardRecto
                  title={card.data.card.title}
                  cardType={card.data.card.card_type}
                  content={card.data.card.structured_content}
                  levels={card.data.card.levels}
                  colorTheme={card.data.card.color_theme}
                  evolved={evolved}
                  interactiveVisuals
                />
              }
            />
            {/* Animation publiée de la carte (lot 4) : « Débloquer » mène aux offres ci-dessous. */}
            <CardAnimationBlock cardId={card.data.id} onUnlock={scrollToUnlockPanel} />
            {/* Les panneaux s'affichent tout de suite ; seul le retournement attend. */}
            <div className="space-y-4">
              <CardDetails
                data={card.data}
                auraPreview={auraPreview}
                onAuraPreview={setAuraPreview}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Fin de la partie gratuite de l'animation : au bloc « Débloque ta SKILLCARD ». */
function scrollToUnlockPanel() {
  document
    .querySelector('[data-testid="skillcard-unlock-panel"]')
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function CardDetails({
  data,
  auraPreview,
  onAuraPreview,
}: {
  data: PublicCard;
  auraPreview: number | null;
  onAuraPreview: (power: number) => void;
}) {
  const { card } = data;
  return (
    <>
      {data.archived && (
        <p className="bg-accent-50 text-accent-600 rounded-xl px-3 py-2 text-sm">
          Cette carte a été archivée par son auteur : son contenu reste consultable.
        </p>
      )}

      <ProgressBlock
        cardId={data.id}
        xpMax={card.xp_max}
        questionXp={card.question_xp}
        levels={card.levels}
        colorTheme={card.color_theme}
        exercises={data.exercises}
        auraPreview={auraPreview}
        onAuraPreview={onAuraPreview}
      />

      <CardCircleBoard cardId={data.id} cardTitle={stripMath(card.title)} />

      <RuleSection data={data} />

      <FiguresPanel data={data} />

      <ToolsPanel data={data} />

      {data.lessons.length > 0 && (
        <Panel icon={<BookOpen className="h-5 w-5" />} title="Cours">
          <div className="space-y-2">
            {data.lessons.map((l) => (
              <Expandable key={l.id} title={l.title}>
                <LessonMarkdown>{l.content_md}</LessonMarkdown>
              </Expandable>
            ))}
          </div>
        </Panel>
      )}

      <RatingPanel cardId={data.id} rating={data.rating} />

      <FiliationPanel parents={data.parents} children={data.children} />
    </>
  );
}

function RarityStars({ rarity }: { rarity: CardRarity | null }) {
  if (!rarity || !(rarity in CARD_RARITY_STARS)) return null;
  const n = CARD_RARITY_STARS[rarity];
  return (
    <span
      className="shrink-0 text-xs tracking-wider text-amber-500"
      aria-label={`Rareté : ${CARD_RARITY_LABELS[rarity]}`}
    >
      {'★'.repeat(n)}
      <span className="text-ink-200">{'★'.repeat(5 - n)}</span>
    </span>
  );
}

function RelativeLink({ card, icon }: { card: PublicCardRelative; icon: ReactNode }) {
  return (
    <Link
      to="/card/$cardId"
      params={{ cardId: card.id }}
      className="hover:bg-ink-50 text-ink-800 flex min-h-[2.75rem] items-center gap-2 rounded-lg px-2 text-sm font-medium active:scale-[0.99]"
    >
      <span className="text-primary-500 shrink-0">{icon}</span>
      <span className="min-w-0 flex-1 truncate">
        <MathText>{card.title}</MathText>
      </span>
      <RarityStars rarity={card.rarity} />
    </Link>
  );
}

/**
 * Filiation (migrations 0275 → 0283 N-N) : les cartes mères (outils plus
 * simples ; la plus rare a une étoile de moins) et les cartes dérivées (suites
 * plus avancées). Seulement les cartes publiées (RPC `get_public_card`).
 */
function FiliationPanel({
  parents,
  children,
}: {
  parents: PublicCardRelative[];
  children: PublicCardRelative[];
}) {
  if (parents.length === 0 && children.length === 0) return null;
  return (
    <Panel icon={<GitBranch className="h-5 w-5" />} title="Filiation">
      <div className="space-y-3">
        {parents.length > 0 && (
          <div>
            <p className="text-ink-500 px-2 text-xs font-semibold">
              {parents.length > 1 ? 'Cartes mères' : 'Carte mère'}
            </p>
            <ul>
              {parents.map((p) => (
                <li key={p.id}>
                  <RelativeLink card={p} icon={<ArrowUpLeft className="h-4 w-4" />} />
                </li>
              ))}
            </ul>
          </div>
        )}
        {children.length > 0 && (
          <div>
            <p className="text-ink-500 px-2 text-xs font-semibold">
              {children.length > 1 ? 'Cartes dérivées' : 'Carte dérivée'}
            </p>
            <ul>
              {children.map((c) => (
                <li key={c.id}>
                  <RelativeLink card={c} icon={<ArrowDownRight className="h-4 w-4" />} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Panel>
  );
}

/**
 * Popularité (migration 0283) : moyenne publique + nombre d'avis ; un compte
 * qui a travaillé / reçu la carte (ou un prof) peut noter de 1 à 5 et changer
 * d'avis. Pictogramme `Star` plein couleur primaire — distinct des ★ dorées de
 * rareté — et toujours avec le nombre d'avis.
 */
function RatingPanel({ cardId, rating }: { cardId: string; rating: PublicCardRating }) {
  const { session } = useAuth();
  const rate = useRateCard(cardId);
  const [hover, setHover] = useState<number | null>(null);
  const avgLabel = formatCardRating(rating.avg);
  const shown = hover ?? rating.mine ?? 0;
  return (
    <Panel icon={<Star className="h-5 w-5" />} title="Utile ?">
      <p
        className="text-ink-700 flex items-baseline gap-2 text-sm"
        data-testid="card-rating-summary"
      >
        {avgLabel ? (
          <>
            <span className="text-ink-500">Utilité</span>
            <span className="text-ink-900 font-display text-2xl font-bold">{avgLabel}</span>
            <span className="text-ink-500">/ {CARD_RATING_MAX}</span>
          </>
        ) : null}
        <span className="text-ink-500">{formatCardRatingCount(rating.count)}</span>
      </p>
      {rating.can_rate ? (
        <div>
          <p className="text-ink-700 text-sm font-semibold">
            Cette carte t’a-t-elle été utile ?
            {rating.mine ? (
              <span className="text-ink-500 font-normal"> (tu peux changer d’avis)</span>
            ) : null}
          </p>
          <div
            role="radiogroup"
            aria-label="Utilité de la carte"
            className="flex"
            onMouseLeave={() => setHover(null)}
          >
            {Array.from({ length: CARD_RATING_MAX }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating.mine === n}
                aria-label={`${cardUsefulnessLabel(n)} (${n} sur ${CARD_RATING_MAX})`}
                disabled={rate.isPending}
                onMouseEnter={() => setHover(n)}
                onClick={() => rate.mutate(n)}
                className="flex h-11 w-11 items-center justify-center rounded-lg active:scale-95 disabled:opacity-60"
              >
                <Star
                  className={cn(
                    'h-7 w-7 transition-colors',
                    n <= shown ? 'fill-primary-500 text-primary-500' : 'text-ink-300',
                  )}
                />
              </button>
            ))}
          </div>
          <p className="text-ink-500 min-h-[1.25rem] text-xs" aria-live="polite">
            {cardUsefulnessLabel(shown) ??
              'Touche une étoile : de « Pas utile » à « Indispensable ».'}
          </p>
          {rate.error && (
            <p role="alert" className="text-accent-600 text-sm">
              {rate.error.message}
            </p>
          )}
        </div>
      ) : (
        <p className="text-ink-500 text-xs">
          {session
            ? 'Travaille cette carte (ou reçois-la de ton prof) pour dire si elle t’a été utile.'
            : 'Connecte-toi après avoir travaillé cette carte pour dire si elle t’a été utile.'}
        </p>
      )}
    </Panel>
  );
}

function Panel({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
      <h2 className="text-ink-900 font-display flex items-center gap-2 text-base font-bold">
        <span className="text-primary-500">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Expandable({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-ink-100 rounded-xl border">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="text-ink-800 flex min-h-[2.75rem] w-full items-center justify-between gap-2 px-3 text-left text-sm font-semibold"
      >
        {title}
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

/** Panneau réservé à l'admin, replié par défaut (en-tête = bouton). */
function AdminPanel({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="border-primary-200 rounded-2xl border border-dashed bg-white">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="text-ink-900 font-display flex min-h-[2.75rem] w-full items-center justify-between gap-2 p-4 text-left text-base font-bold"
      >
        <span className="flex items-center gap-2">
          <span className="text-primary-500">
            <Wrench className="h-5 w-5" />
          </span>
          Admin
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && <div className="space-y-3 px-4 pb-4">{children}</div>}
    </section>
  );
}

/** Figures à contraintes du recto, ici manipulables (sur la carte elles sont figées). */
function FiguresPanel({ data }: { data: PublicCard }) {
  const figures = manipulableCardVisuals(data.card.structured_content);
  if (figures.length === 0) return null;
  return (
    <Panel
      icon={<Shapes className="h-5 w-5" />}
      title={figures.length > 1 ? 'Manipuler les figures' : 'Manipuler la figure'}
    >
      <p className="text-ink-500 text-sm">
        Déplace les points ou les curseurs pour voir la propriété rester vraie.
      </p>
      {figures.map((section, i) => (
        <figure key={i} className="space-y-1">
          <VisualBlockRenderer block={section.block as unknown as ExerciseVisualBlock} />
          {section.caption ? (
            <figcaption className="text-ink-500 text-xs">{section.caption}</figcaption>
          ) : null}
        </figure>
      ))}
    </Panel>
  );
}

/**
 * Outils de la Boîte à outils reliés à la carte par son auteur (migration
 * 0551, N-N) : toucher un outil l'ouvre dans la boîte, au niveau de l'élève
 * connecté (sinon au plus avancé de la carte). Ouvert à tous, même sans compte.
 */
function ToolsPanel({ data }: { data: PublicCard }) {
  const { session, profile } = useAuth();
  const studentProfile = useStudentProfile(
    profile?.role === 'student' ? (session?.user?.id ?? undefined) : undefined,
  );
  const [openTool, setOpenTool] = useState<ToolId | null>(null);
  const tools = useMemo(() => linkedTools(data.tools), [data.tools]);
  const level = toolLevelForCard(
    data.card.levels,
    profile?.role === 'student' ? (studentProfile.data?.level ?? null) : null,
  );
  const context = useMemo(
    () => contextForCards([{ ...data.card, tools: data.tools }], level),
    [data.card, data.tools, level],
  );
  if (tools.length === 0) return null;
  return (
    <Panel icon={<Shapes className="h-5 w-5" />} title="Boîte à outils">
      <p className="text-ink-500 text-sm">
        {tools.length > 1
          ? 'Manipule la notion avec les outils choisis pour cette carte.'
          : 'Manipule la notion avec l’outil choisi pour cette carte.'}
      </p>
      <ToolList tools={tools} onOpen={setOpenTool} />
      {openTool && (
        <ToolboxSheet
          context={context}
          initialTool={openTool}
          featuredTitle="Pour cette SKILLCARD"
          onClose={() => setOpenTool(null)}
        />
      )}
    </Panel>
  );
}

function RuleSection({ data }: { data: PublicCard }) {
  if (cardRuleParts(data.card).length === 0) return null;
  return (
    <Panel icon={<ScrollText className="h-5 w-5" />} title="Règle détaillée">
      <Expandable title="Lire la règle détaillée">
        <CardRuleDetails card={data.card} />
      </Expandable>
    </Panel>
  );
}

/**
 * Visiteur non connecté : invitation à se connecter, EN HAUT de la page (sans
 * panneau). Connexion sur place (OTP) ; `setReturnTo` couvre OAuth et un
 * nouveau compte (onboarding), qui reviennent ensuite sur cette carte.
 */
function CardLoginPrompt({ cardId }: { cardId: string }) {
  const [showLogin, setShowLogin] = useState(false);
  return (
    <div className="space-y-3 text-center">
      <p className="font-display text-ink-900 text-lg font-bold">
        Connecte-toi pour maîtriser la carte !
      </p>
      {showLogin ? (
        <div className="text-left">
          <OtpAuthForm
            renderHeader={() => (
              <p className="text-ink-900 font-display text-base font-bold">
                Connexion ou création de compte — tu reviendras sur cette carte
              </p>
            )}
          />
        </div>
      ) : (
        <Button
          fullWidth
          onClick={() => {
            setReturnTo(`/card/${cardId}`);
            setShowLogin(true);
          }}
        >
          Se connecter
        </Button>
      )}
    </div>
  );
}

function ProgressBlock({
  cardId,
  xpMax,
  questionXp,
  levels,
  colorTheme,
  exercises,
  auraPreview,
  onAuraPreview,
}: {
  cardId: string;
  xpMax: number;
  questionXp: number;
  levels: string[];
  colorTheme: CardColorTheme;
  exercises: PublicCardExercise[];
  auraPreview: number | null;
  onAuraPreview: (power: number) => void;
}) {
  const { session, profile, profileLoading } = useAuth();
  // Teinte de la carte : aura de l'avatar + lignes d'exercice.
  const palette = cardPalette(levels, colorTheme);
  const progress = useMyCardProgress(cardId, !!session && !!profile);
  // Accès SKILLCARD (migration 0304) : gratuit / à débloquer / dans le deck.
  const skAccess = useSkillcardAccess(cardId, session?.user?.id ?? null);
  const exerciseAccess = skAccess.data
    ? {
        allowed: skAccess.data.allowed,
        freeExerciseId: skAccess.data.free_exercise_id,
        freeQuestions: skAccess.data.free_questions,
      }
    : undefined;
  // Niveau de l'élève : il ne voit que les exercices de son niveau ou inférieurs.
  const studentProfile = useStudentProfile(
    profile?.role === 'student' ? (session?.user?.id ?? undefined) : undefined,
  );
  const studentLevel = profile?.role === 'student' ? (studentProfile.data?.level ?? null) : null;
  // Où en est-il sur chaque exercice : fait N fois, à continuer.
  const myAttempts = useMyExerciseAttempts(
    profile ? (session?.user?.id ?? null) : null,
    exercises.map((e) => e.id),
  );

  // Connexion réussie SUR cette page : le retour automatique devient inutile.
  // Onglet visible seulement : en arrière-plan, la session vient d'un AUTRE
  // onglet, dont on effacerait le retour (achat express…).
  useEffect(() => {
    if (session && profile && isActiveTab()) consumeReturnTo();
  }, [session, profile]);

  // Nouveau compte (session sans profil) : onboarding, puis retour ici — dans
  // l'onglet visible seulement (cf. `isActiveTab`).
  useEffect(() => {
    if (!session || profileLoading || profile || !isActiveTab()) return;
    setReturnTo(`/card/${cardId}`);
    window.location.href = '/onboarding';
  }, [session, profileLoading, profile, cardId]);

  if (!session) {
    return (
      <>
        <Panel icon={<Dumbbell className="h-5 w-5" />} title="Exercices">
          <CardExerciseList
            exercises={exercises}
            questionXp={questionXp}
            xpMax={xpMax}
            palette={palette}
            access={exerciseAccess}
            cardId={cardId}
            // Visiteur : l'exercice demande une connexion (/login) ; on revient
            // ensuite sur cette carte au lieu du tableau de bord.
            onOpen={() => setReturnTo(`/card/${cardId}`)}
          />
        </Panel>
        <SkillcardUnlockPanel
          cardId={cardId}
          userId={null}
          access={skAccess.data}
          palette={palette}
        />
      </>
    );
  }

  if (!profile || progress.isLoading) {
    return (
      <Panel icon={<Dumbbell className="h-5 w-5" />} title="Exercices">
        <div className="bg-ink-100 h-16 animate-pulse rounded-xl" />
      </Panel>
    );
  }

  const xp = progress.data?.xp ?? 0;
  const max = progress.data?.xp_max ?? xpMax;
  const mastery = progress.data?.mastery ?? 0;
  const used = (progress.data?.usage_count ?? 0) > 0;

  const auraPower = Math.round(auraPreview ?? cardAuraScore(mastery, xp, max));
  return (
    <>
      <Panel icon={<Dumbbell className="h-5 w-5" />} title="Exercices">
        <CardExerciseList
          exercises={exercises}
          questionXp={questionXp}
          xpMax={max}
          currentXp={xp}
          studentLevel={studentLevel}
          attempts={myAttempts.data}
          palette={palette}
          access={exerciseAccess}
          cardId={cardId}
          notes={
            !used && (
              <p className="text-ink-500 text-xs">
                Réponds juste aux questions d’exercices reliées à cette carte pour gagner de l’HORA.
              </p>
            )
          }
        />
      </Panel>
      {/* Tous les comptes : seul l'admin (ou le tuteur auteur) a tout gratuitement (0310). */}
      <SkillcardUnlockPanel
        cardId={cardId}
        userId={session.user?.id ?? null}
        access={skAccess.data}
        palette={palette}
      />
      {/* Prof / admin : offrir la carte à des élèves ou des classes (0355). */}
      <TrainStudentsPanel cardId={cardId} cardLevels={levels} />
      {profile.role === 'admin' && (
        <AdminPanel>
          <Link
            to="/tutor/cards/$cardId"
            params={{ cardId }}
            className="border-primary-200 text-primary-700 hover:bg-primary-50 flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-semibold active:scale-[0.98]"
          >
            <Pencil className="h-4 w-4" aria-hidden />
            Modifier la SKILLCARD
          </Link>
          <label className="text-ink-600 flex items-center gap-3 text-sm">
            <span className="shrink-0">Aperçu de l’aura</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={auraPower}
              onChange={(e) => onAuraPreview(Number(e.target.value))}
              className="accent-primary-500 min-h-[2.75rem] flex-1"
            />
            <span className="text-ink-900 w-12 text-right font-bold">{auraPower} %</span>
          </label>
          <CardExerciseGenerator cardId={cardId} levels={levels} />
        </AdminPanel>
      )}
    </>
  );
}
